import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  MapPin,
  AlertTriangle,
  X,
} from "lucide-react";
import { useQuery, useMutation } from "@apollo/client/react";

import { GET_CUSTOMER_LOCATIONS } from "../features/location/locationQueries";
import { DELETE_CUSTOMER_LOCATION } from "../features/location/locationMutations";

interface CustomerLocation {
  id: string;
  location: string;
}

interface CustomerLocationsResponse {
  customerLocations: CustomerLocation[];
}

export default function LocationsScreen() {
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery<CustomerLocationsResponse>(
    GET_CUSTOMER_LOCATIONS,
    {
      fetchPolicy: "network-only",
    },
  );

  const [deleteCustomerLocation] = useMutation(DELETE_CUSTOMER_LOCATION);

  const locations = data?.customerLocations ?? [];

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [locationToDelete, setLocationToDelete] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const openDeleteModal = (id: string, label: string) => {
    setLocationToDelete({ id, label });
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!locationToDelete) return;

    try {
      await deleteCustomerLocation({
        variables: {
          id: locationToDelete.id,
        },
      });

      await refetch();

      setIsModalOpen(false);
      setLocationToDelete(null);
    } catch (error) {
      console.error("Delete location error:", error);
    }
  };

  return (
    <div className="min-h-screen md:min-h-0 bg-gray-50 md:bg-transparent pb-10 relative md:max-w-3xl md:mx-auto">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-5 shadow-sm rounded-b-[32px] md:rounded-3xl mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center bg-white active:scale-95 transition-transform shadow-sm"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>

            <div>
              <h1 className="text-lg font-bold font-poppins text-gray-900 leading-none mb-1">
                Locations
              </h1>

              <p className="text-xs text-gray-400 font-medium">
                Manage your addresses
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/profile/locations/add")}
            className="w-11 h-11 bg-[#44B6CE] text-white rounded-2xl flex items-center justify-center active:scale-90 transition-transform shadow-lg shadow-cyan-100"
          >
            <Plus size={24} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-5 md:px-0">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-4 border-[#44B6CE] border-t-transparent animate-spin" />
          </div>
        ) : locations.length === 0 ? (
          <div className="bg-white rounded-[32px] border border-dashed border-gray-200 p-10 text-center flex flex-col items-center">
            <div className="w-20 h-20 rounded-3xl bg-[#44B6CE]/10 flex items-center justify-center mb-6">
              <MapPin size={32} className="text-[#44B6CE]" />
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-2">
              No Locations Added
            </h3>

            <p className="text-sm text-gray-400 leading-relaxed mb-8 max-w-[200px]">
              Save your pickup & delivery addresses for faster ordering.
            </p>

            <button
              onClick={() => navigate("/profile/locations/add")}
              className="w-full h-14 bg-[#44B6CE] text-white rounded-2xl flex items-center justify-center gap-2 font-bold shadow-lg shadow-cyan-50"
            >
              <Plus size={20} strokeWidth={3} />
              <span>Add Your First Location</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {locations.map((loc) => (
              <div
                key={loc.id}
                className="bg-white rounded-[28px] p-5 border border-gray-100 shadow-sm flex items-center gap-4"
              >
                {/* Icon Section */}
                <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#44B6CE]" />
                </div>

                {/* Text Content Section */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-base mb-0.5">
                    {/* Dynamically show "Home", "Office", etc. */}
                    {loc.location.split(" - ")[0]}
                  </p>

                  <p className="text-sm text-gray-500 leading-snug">
                    {/* Show the rest of the address string */}
                    {loc.location.split(" - ")[1] || loc.location}
                  </p>
                </div>

                {/* Delete Button Section */}
                <div className="flex items-center justify-center">
                  <button
                    onClick={() => openDeleteModal(loc.id, "Saved Address")}
                    className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 active:bg-red-100 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setIsModalOpen(false)}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-sm bg-white rounded-[32px] p-6 shadow-2xl animate-in slide-in-from-bottom-10 duration-300">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="text-red-500" size={32} />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Delete Location?
              </h3>

              <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                Are you sure you want to remove{" "}
                <span className="font-bold text-gray-700">
                  "{locationToDelete?.label}"
                </span>
                ? This action cannot be undone.
              </p>

              <div className="w-full space-y-3">
                <button
                  onClick={confirmDelete}
                  className="w-full h-14 bg-red-500 text-white rounded-2xl font-bold shadow-lg shadow-red-100 active:scale-[0.98] transition-transform"
                >
                  Yes, Delete
                </button>

                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full h-14 bg-gray-50 text-gray-500 rounded-2xl font-bold active:scale-[0.98] transition-transform"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
